import { BuilderFeatures } from '../../BuilderFeatures';
import { PlanId, PlanReason, planResourceType, PlanStatus } from '../types/plan';
import { ContractActivationOrigins } from './StudioSegmentService';
import { defaultClusterName } from 'app.constants';
const APA_HOSTING = 'postmaster@aiplatform.msging.net';
/**
 * Service for handling Studio-related operations, including trial plan registration,
 * plan status checking, and feature availability based on tenant plans.
 */
export class StudioService {
    static $inject = ['ContextProcessorService', 'StudioSegmentService', 'BlipService', 'TenantService'];
    constructor(ContextProcessorService, StudioSegmentService, BlipService, TenantService) {
        'ngInject';
        this.ContextProcessorService = ContextProcessorService;
        this.StudioSegmentService = StudioSegmentService;
        this.BlipService = BlipService;
        this.TenantService = TenantService;
    }
    //#endregion
    //#region Public Methods
    /**
     * Registers a listener function that will be called when the studio trial status changes.
     * @param {function} callback - The function to call when the status changes.
     * @returns {function} A function that can be called to unsubscribe the listener.
     */
    onStatusChange(callback) {
        StudioService.statusListeners.push(callback);
        // Return unsubscribe function
        return () => {
            const index = StudioService.statusListeners.indexOf(callback);
            if (index !== -1) {
                StudioService.statusListeners.splice(index, 1);
            }
        };
    }
    /**
     * Applies a trial plan to the current tenant and tracks the registration event.
     * @returns {Promise<void>} A promise that resolves when the trial plan is applied.
     */
    async applyTrialToTenant(payload) {
        await this.registerTrialPlan();
        this.StudioSegmentService.trackTrialRegisterPlan(payload);
        // Notify listeners that the trial status has changed
        StudioService.planStatusCache = null;
        this.notifyStatusChange();
    }
    /**
     * Initiates the upgrade plan process by tracking the upgrade event.
     * @param {Object} payload - Additional data to include in the tracking event.
     * @returns {Promise<void>} A promise that resolves when the tracking is complete.
     */
    async upgradePlan(payload) {
        const planInfo = await this.getPlanByTenant();
        const planStatus = await this.getPlanStatusByTenant();
        const aditionalInfo = {
            ...payload,
            ...planInfo,
            ...planStatus,
        };
        this.StudioSegmentService.trackUpgradePlan(aditionalInfo);
    }
    /**
     * Retrieves the plan information for the current tenant.
     * @returns {Promise<unknown>} A promise that resolves to the plan data.
     */
    async getPlanByTenant() {
        return this.getPlan();
    }
    async getPlanStatusByTenant() {
        return this.getPlanStatus();
    }
    /**
     * Determines whether the trial register option should be shown based on studio features and plan status.
     * @returns {Promise<boolean>} A promise that resolves to true if the option should be shown, false otherwise.
     */
    async shouldShowTrialRegisterOption() {
        const isAdmin = await this.TenantService.isContractAdmin(this.ContextProcessorService.currentUser.identity);
        if (!isAdmin) {
            return PlanStatus.NONE;
        }
        const isStudioCheckingEnabled = await this.isStudioCheckingEnabled();
        if (!isStudioCheckingEnabled) {
            return PlanStatus.NONE;
        }
        const planStatus = await this.getPlanStatus();
        if (planStatus?.hasActivePaidPlan) {
            return PlanStatus.ACTIVE; // Already purchased the product
        }
        if (!planStatus?.hasActivePaidPlan) {
            if (planStatus?.reason === PlanReason.NOT_FOUND) {
                return PlanStatus.AVAILABLE_TO_TRIAL; // Can purchase the product, will return true enum
            }
            if (planStatus?.reason === PlanReason.CANCELED || planStatus?.isTrial) {
                return PlanStatus.AVAILABLE_TO_UPGRADE; // In canceled, trial has ended
            }
        }
        return PlanStatus.NONE;
    }
    /**
     * Gets banner display information based on the current plan status.
     * @returns {Promise<{shouldShowTrialBanner: boolean, shouldShowUpgradeBanner: boolean}>}
     * An object indicating which banners should be displayed.
     */
    async getBannerDisplayInfo() {
        const planStatus = await this.shouldShowTrialRegisterOption();
        await this.trackStudioActivation(ContractActivationOrigins.ApplicationList);
        return {
            shouldShowTrialBanner: planStatus === PlanStatus.AVAILABLE_TO_TRIAL,
            shouldShowUpgradeBanner: planStatus === PlanStatus.AVAILABLE_TO_UPGRADE,
            planStatus: planStatus,
        };
    }
    async shouldEnableStudioFeatures() {
        const planStatus = await this.getPlanStatus();
        if (planStatus?.reason === PlanReason.NOT_CONFIGURED) {
            const blipIDFlag = await BuilderFeatures.isAiAgentCreateBuilderBlockEnabled();
            return blipIDFlag;
        }
        const isFeatureEnabled = (planStatus?.hasActivePaidPlan || planStatus?.isTrial) ?? false;
        return isFeatureEnabled;
    }
    /**
     * Determines whether studio plan feature toggle checking is enabled for the current tenant.
     * @returns A promise that resolves to `true` if studio plan feature toggle is enabled, otherwise `false`.
     */
    async isStudioCheckingEnabled() {
        if (StudioService.ftCheckPromise) {
            return StudioService.ftCheckPromise;
        }
        StudioService.ftCheckPromise = BuilderFeatures.isStudioCheckingEnabled();
        return StudioService.ftCheckPromise;
    }
    /**
     * Checks if the trial for the tenant has expired.
     * @returns {Promise<boolean>} A promise that resolves to true if the trial is expired, false otherwise.
     */
    async isTrialExpired() {
        const response = await this.getPlanStatus();
        return !response?.isActive && response?.isTrial && response?.reason === PlanReason.BLOCKED;
    }
    //#endregion
    //#region Private Methods
    /**
     * Notifies all registered listeners that the trial status has changed.
     * @private
     */
    notifyStatusChange() {
        StudioService.statusListeners.forEach(callback => callback());
    }
    /**
     * Retrieves the tenant ID from the context processor service.
     * @private
     * @returns {string} The tenant ID.
     */
    getTenantId() {
        return this.ContextProcessorService.tenant.id;
    }
    /**
     * Registers a trial plan for the current tenant.
     * @private
     * @returns {Promise<void>} A promise that resolves when the trial plan is registered.
     */
    async registerTrialPlan() {
        const resource = {
            tenantId: this.getTenantId(),
            planId: PlanId.TRIAL,
        };
        const command = {
            method: 'set',
            uri: '/agent-plan',
            to: APA_HOSTING,
            type: planResourceType,
            resource,
        };
        await this.BlipService.processCommand(command);
    }
    /**
     * Retrieves the active plan status for the current tenant.
     * @private
     * @returns {Promise<PlanResourceStatusResponse | null>} A promise that resolves to the plan status or null if not found.
     */
    async getPlanStatus() {
        this.resetStaticCacheIfTenantChanged();
        if (StudioService.planStatusCache) {
            return StudioService.planStatusCache;
        }
        // If there's already a request in flight, return that promise
        if (StudioService.getPlanStatusPromise) {
            return StudioService.getPlanStatusPromise;
        }
        // Create and store the promise for the request
        StudioService.getPlanStatusPromise = (async () => {
            try {
                const command = {
                    method: 'get',
                    uri: `/agent-plan/${this.getTenantId()}/active`,
                    to: APA_HOSTING,
                };
                const response = await this.BlipService.processCommand(command);
                if (response.resource) {
                    StudioService.planStatusCache = response.resource;
                    return StudioService.planStatusCache;
                }
                return null;
            }
            finally {
                // Clear the in-flight promise after completion
                StudioService.getPlanStatusPromise = null;
            }
        })();
        return StudioService.getPlanStatusPromise;
    }
    /**
     * Retrieves the plan details for the current tenant.
     * @private
     * @returns {Promise<unknown>} A promise that resolves to the plan data.
     */
    async getPlan() {
        const command = {
            method: 'get',
            uri: `/agent-plan/${this.getTenantId()}`,
            to: APA_HOSTING,
        };
        const response = await this.BlipService.processCommand(command);
        return response.resource;
    }
    resetStaticCacheIfTenantChanged() {
        const currentTenantId = this.getTenantId();
        if (StudioService.lastCheckedTenantId !== currentTenantId) {
            StudioService.planStatusCache = null;
            StudioService.getPlanStatusPromise = null;
            StudioService.ftCheckPromise = null;
            StudioService.lastCheckedTenantId = currentTenantId;
            StudioService.subscriptionTrackingOrigin = {
                applicationList: false,
                builder: false,
                applicationHome: false,
            };
            StudioService.statusListeners = [];
        }
    }
    async trackStudioActivation(origin) {
        if (StudioService.subscriptionTrackingOrigin[origin]) {
            return;
        }
        const planStatus = await this.getPlanStatus();
        const planReason = this.mapPlanStatusToPlanReason(planStatus);
        this.StudioSegmentService
            .trackContractStudioActivated({
            origin,
            planId: planReason,
            cluster: defaultClusterName,
            tenantId: this.ContextProcessorService.tenant.id,
        });
        StudioService.subscriptionTrackingOrigin[origin] = true;
    }
    mapPlanStatusToPlanReason(planResource) {
        if (!planResource) {
            return PlanReason.NOT_FOUND;
        }
        if (planResource?.hasActivePaidPlan) {
            return PlanReason.PAID;
        }
        if (planResource.isConsumptionBlocked) {
            return PlanReason.BLOCKED;
        }
        if (!planResource.isActive) {
            if (planResource.isTrial) {
                return PlanReason.TRIAL;
            }
            if (planResource?.reason) {
                return planResource.reason;
            }
        }
        return PlanReason.NOT_CONFIGURED;
    }
}
//#region Properties
StudioService.statusListeners = [];
StudioService.planStatusCache = null;
StudioService.getPlanStatusPromise = null;
StudioService.ftCheckPromise = null;
StudioService.subscriptionTrackingOrigin = {
    applicationList: false,
    builder: false,
    applicationHome: false,
};
StudioService.lastCheckedTenantId = null;
//#region Constructor
/**
 * Creates an instance of StudioService.
 * @param {ContextProcessorService} ContextProcessorService - Service for processing context and tenant information.
 * @param {StudioSegmentService} StudioSegmentService - Service for tracking studio-related events.
 * @param {BlipService} BlipService - Service for processing messaging commands.
 */
StudioService.$inject = ['ContextProcessorService', 'StudioSegmentService', 'BlipService', 'TenantService'];
